import React, { useState, useEffect, useRef } from 'react';
import { DynamicFilterButtons } from '../shared/DynamicFilterButtons';
import {
  TwoDElementsIcon,
  ThreeDElementsIcon,
  AssetIcon,
  Essentials,
  ListIcon,
  GridIcon,
  ExitIcon
} from '../../modules/Icons';
import Link from 'next/link';
import { EssentialsCategoryModalProps } from '../../types/components/collections/EssentialsCategoryModal';
import VideoCover from 'react-video-cover';
import DetectOutsideClick from '../helpers/DetectOutsideClick';
import { Dialog } from '@headlessui/react';
import InfinitePagination from '../../modules/InfinitePagination';
import EssentialsMonthlyMarker from '../shared/EssentialsMonthlyMarker';

export const EssentialsCategoryModal: React.FC<EssentialsCategoryModalProps> = ({
  collections,
  modalOpen,
  setModalOpen,
  total_count,
  total_2D,
  total_3D,
  loadNextPage,
  allPagesLoaded,
  onLibrarySelected,
}) => {
  const [categoryFilter, setCategoryFilter] = useState('');
  const [gridView, setGridView] = useState(true);

  const onCategoryFilterApplied = (filter: string) => {
    setCategoryFilter(filter);
    if (!onLibrarySelected) return;
    let libraryId: number;
    switch (filter) {
      case '2D':
        libraryId = 1;
        break;
      case '3D':
        libraryId = 3;
        break;
      default:
        break;
    }
    onLibrarySelected(libraryId);
  }

  const CollectionItem = ({ collection }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const validPreview = Boolean(collection.media.mp4);

    const videoOptions = {
      src: collection.media.mp4,
      muted: true,
      playsInline: true,
      preload: 'none',
      loop: true,
      ref: videoRef,
      'data-testid': 'collectionVideoPreview'
    };

    const onHover = async () => {
      if (!validPreview || !videoRef.current) return;
      try {
        videoRef.current.classList.remove('hidden');
        await videoRef.current.play();
      } catch (err) {
        console.warn(err);
      }
    };

    const onLeave = () => {
      if (!validPreview || !videoRef.current) return;
      videoRef.current.classList.add('hidden');
      videoRef.current.currentTime = 0;
      videoRef.current.pause();
    };

    return (
      <Link
        className="text-a-lighter-gray hover:text-white !fill-[#AFAFB0] hover:!fill-white relative"
        href={`/collections/${collection.slug}`}
        prefetch={false}
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
      >
        <div
          className="overflow-hidden rounded-[5px] relative border border-transparent hover:border-[#4D5457] hover:shadow-md"
          data-testid="collectionCard"
        >
          <div className="absolute top-3 left-3 z-50 rounded-[3px] overflow-hidden">
            <EssentialsMonthlyMarker essentialsMonthlyExpiryDate={collection.ec_schedule_end_at} size="sm"/>
          </div>
          <div className="absolute top-0 h-full w-full rounded-[5px] overflow-hidden">
            {validPreview && <VideoCover videoOptions={videoOptions} />}
          </div>
          <img
            src={collection.media.image}
            width="600"
            height="338"
            className="block bg-slate-900"
            alt={collection.name}
          />
        </div>
        <div className="flex items-start">
          <div className="flex grow">
            <h2 className="text-[12px] sm:text-[14px] py-2 px-1 leading-snug inter">
              {collection.name}
            </h2>
          </div>
          <div className="flex flex-nowrap flex-col pt-2 sm:flex-row sm:gap-1 text-right justify-items-start sm:justify-self-end items-end sm:items-center text-[10px] w-full sm:w-auto sm:text-[13px]">
            <AssetIcon />
            <span className="whitespace-nowrap">{collection.elements}</span>
          </div>
        </div>
      </Link>
    );
  };

  return (
    <Dialog open={modalOpen} onClose={setModalOpen} className="relative z-40">
      <DetectOutsideClick onOutsideClick={() => setModalOpen(false)}>
        <div className="fixed inset-0 bg-black/80" aria-hidden="true" />

        <div className="fixed inset-0 overflow-y-auto" cy-test-id="essentials-category-modal">
          <div className="flex items-center justify-center min-h-full p-4">
            <Dialog.Panel className="relative w-full max-w-[1490px] max-h-[896px] rounded-[10px] bg-a-dark-gray text-white flex flex-col">
              <div className="absolute z-10 cursor-pointer top-4 right-4" onClick={() => setModalOpen(false)}>
                <ExitIcon />
              </div>

              <div className="flex-shrink-0">
                <div className="flex flex-col items-center pt-12 pb-4 sm:pb-6 md:pb-8">
                  <h5 className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 md:text-[36px] font-bold mb-3 sm:mb-4 leading-tight sm:leading-[44px] tracking-wide uppercase text-center sm:text-left text-white">
                    <span>The</span>
                    <Essentials className="w-32 h-min sm:w-40 md:w-48 lg:w-auto" />
                    <span>Catalog</span>
                  </h5>
                  <p className="mb-4 sm:mb-6 w-full max-w-[770px] text-[12px] md:text-[18px] text-a-gray font-light leading-snug sm:leading-normal md:leading-relaxed lg:leading-[28px] text-center px-4 sm:px-6">
                    Subscribe and get access to over 3000 premium 2D & 3D assets. Freely download these assets
                    without spending any credits!
                  </p>
                </div>

                <section className="items-center justify-between px-4 pb-6 md:flex md:px-10">
                  <DynamicFilterButtons
                    activeFilter={categoryFilter}
                    setFilter={onCategoryFilterApplied}
                    buttonsConfig={[
                      { category: '', label: 'All', total: total_count },
                      { category: '2D', label: '2D Elements', IconComponent: TwoDElementsIcon, total: total_2D },
                      { category: '3D', label: '3D Assets', IconComponent: ThreeDElementsIcon, total: total_3D }
                    ]}
                  />
                  <div className="flex max-w-[76px] bg-gray-900 rounded-[5px] p-[10px] gap-2 cursor-pointer">
                    <ListIcon onClick={() => setGridView(false)} stroke={!gridView ? '#F5F5F5' : '#4D5457'} />
                    <GridIcon onClick={() => setGridView(true)} stroke={gridView ? '#F5F5F5' : '#4D5457'} />
                  </div>
                </section>
              </div>

              <section className="flex-grow overflow-y-auto scrollbar-custom">
                <div className="px-4 md:px-10">
                  {gridView ? (
                    <div className="grid w-full grid-cols-2 gap-4 pb-6 sm:grid-cols-3 xl:grid-cols-4">
                      {collections?.map((collection, k) => (
                        <CollectionItem key={k} collection={collection} />
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center w-full mb-3">
                      <div className="flex gap-6 items-center w-full md:py-1 md:my-1 text-[12px] uppercase text-gray-600">
                        <div className="w-3/5">Collection Title</div>
                      </div>
                      {collections?.map((collection, k) => (
                        <div key={k} className="flex gap-6 items-center w-full py-1 px-2 my-1 rounded-[5px]">
                          <Link
                            className="relative flex items-center w-full gap-6 text-a-lighter-gray hover:text-white grow"
                            href={`/collections/${collection.slug}`}
                            prefetch={false}
                          >
                            <img
                              src={collection.media.image}
                              width="75"
                              height="42.25"
                              className="block bg-slate-900"
                              alt={collection.name}
                            />
                            <h2 className="text-[12px] w-full md:text-[14px] py-2 px-1 leading-snug inter">
                              {collection.name}
                            </h2>
                            <div className="flex gap-1 text-left justify-self-end items-center text-[8px] md:text-[13px] w-full justify-end">
                              <span>{collection.elements}</span>
                              <AssetIcon />
                            </div>
                          </Link>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {!!loadNextPage && !allPagesLoaded && (
                  <InfinitePagination
                    totalPages={999}
                    currentPage={1}
                    loadHref="/products/page/[page]"
                    loadAs="/products/page/$"
                    getContent={loadNextPage}
                  />
                )}
              </section>
            </Dialog.Panel>
          </div>
        </div>
      </DetectOutsideClick>
    </Dialog>
  );
};
